import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  filterRow: { display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px", alignItems: "flex-end" },
  filterControl: { minWidth: "200px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  successMsg: { color: "#4caf50", fontSize: "13px" },
  statCard: { textAlign: "center", padding: "12px", background: "#f5f5f5", borderRadius: "8px" },
  statNum: { fontSize: "28px", fontWeight: "bold", color: "#1a237e", margin: 0 },
  statLabel: { fontSize: "12px", color: "#666", margin: 0 }
};

const STATUS_TONES = {
  enrolled: "success",
  dropped: "danger",
  completed: "info"
};

function creditsByStudent(enrollments) {
  const totals = {};
  enrollments.forEach(e => {
    if (e.status === "dropped") return;
    const studentId = e.student?.id || e.studentId;
    totals[studentId] = (totals[studentId] || 0) + (e.course?.creditUnits || 0);
  });
  return totals;
}

class EnrollmentList extends Component {
  state = {
    enrollments: [],
    semesters: [],
    courses: [],
    programmes: [],
    selectedSemester: "",
    selectedCourse: "",
    selectedProgramme: "",
    loading: false,
    open: false,
    bulkOpen: false,
    form: { studentId: "", courseId: "", semesterId: "" },
    bulkForm: { semesterId: "", programmeId: "", yearOfStudy: "" },
    studentSearch: "",
    studentResults: [],
    searchLoading: false,
    saving: false,
    error: null,
    success: null,
    stats: { total: 0, active: 0, dropped: 0 }
  };

  componentDidMount() {
    this.fetchMeta();
  }

  fetchMeta = async () => {
    try {
      const [sRes, cRes, pRes] = await Promise.all([
        api.get("/semesters?limit=100"),
        api.get("/courses?limit=100"),
        api.get("/programmes?limit=100").catch(() => ({ data: { data: [] } }))
      ]);
      const sd = sRes.data.data || sRes.data;
      const cd = cRes.data.data || cRes.data;
      const pd = pRes.data.data || pRes.data;
      const semesters = Array.isArray(sd) ? sd : sd.data || [];
      const current = semesters.find(s => s.isCurrent);
      this.setState({
        semesters,
        courses: Array.isArray(cd) ? cd : cd.data || [],
        programmes: Array.isArray(pd) ? pd : pd.data || [],
        selectedSemester: current ? current.id : ""
      }, () => { if (this.state.selectedSemester) this.fetchEnrollments(); });
    } catch (e) { /* silent */ }
  };

  fetchEnrollments = async () => {
    const { selectedSemester, selectedCourse, selectedProgramme } = this.state;
    if (!selectedSemester) return;
    this.setState({ loading: true });
    try {
      const params = { semesterId: selectedSemester, limit: 200 };
      if (selectedCourse) params.courseId = selectedCourse;
      if (selectedProgramme) params.programmeId = selectedProgramme;
      const res = await api.get("/enrollments", { params });
      const d = res.data.data || res.data;
      const list = Array.isArray(d) ? d : d.data || [];
      const active = list.filter(e => e.status === "enrolled").length;
      const dropped = list.filter(e => e.status === "dropped").length;
      this.setState({ enrollments: list, loading: false, stats: { total: list.length, active, dropped } });
    } catch (e) { this.setState({ loading: false }); }
  };

  searchStudents = async (q) => {
    if (!q || q.length < 2) { this.setState({ studentResults: [] }); return; }
    this.setState({ searchLoading: true });
    try {
      const res = await api.get("/students", { params: { search: q, limit: 10 } });
      const d = res.data.data || res.data;
      this.setState({ studentResults: Array.isArray(d) ? d : d.data || [], searchLoading: false });
    } catch (e) { this.setState({ searchLoading: false }); }
  };

  handleEnroll = async () => {
    const { form } = this.state;
    if (!form.studentId || !form.courseId || !form.semesterId) {
      this.setState({ error: "All fields are required." }); return;
    }
    this.setState({ saving: true, error: null });
    try {
      await api.post("/enrollments", form);
      this.setState({ open: false, saving: false, success: "Student enrolled successfully." });
      this.fetchEnrollments();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Enrollment failed.", saving: false });
    }
  };

  handleBulkEnroll = async () => {
    const { bulkForm } = this.state;
    if (!bulkForm.semesterId || !bulkForm.programmeId) {
      this.setState({ error: "Semester and programme are required." }); return;
    }
    this.setState({ saving: true, error: null });
    try {
      await api.post("/enrollments/bulk", bulkForm);
      this.setState({ bulkOpen: false, saving: false, success: "Bulk enrollment completed." });
      this.fetchEnrollments();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Bulk enrollment failed.", saving: false });
    }
  };

  handleDrop = async (id) => {
    if (!window.confirm("Drop this enrollment?")) return;
    try {
      await api.patch(`/enrollments/${id}/drop`);
      this.fetchEnrollments();
    } catch (e) { /* silent */ }
  };

  render() {
    const { classes } = this.props;
    const {
      enrollments, semesters, courses, programmes,
      selectedSemester, selectedCourse, selectedProgramme,
      loading, open, bulkOpen, form, bulkForm,
      studentSearch, studentResults, searchLoading,
      saving, error, success, stats
    } = this.state;

    const totalCreditsByStudent = creditsByStudent(enrollments);

    const columns = [
      { id: "stdNo", header: "Std No", accessorFn: e => e.student?.studentNo || "—", meta: { variant: "mono" } },
      {
        id: "studentName",
        header: "Student Name",
        accessorFn: e => `${e.student?.firstName || ""} ${e.student?.lastName || ""}`.trim() || "—"
      },
      { id: "code", header: "Code", accessorFn: e => e.course?.code || "—", meta: { variant: "mono" } },
      { id: "course", header: "Course", accessorFn: e => e.course?.name || "—" },
      { id: "credits", header: "Credits", accessorFn: e => e.course?.creditUnits ?? "—", meta: { align: "right" } },
      {
        id: "totalCredits",
        header: "Total Credits",
        accessorFn: e => totalCreditsByStudent[e.student?.id] || 0,
        meta: { align: "right" }
      },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const status = info.row.original.status || "enrolled";
          return <Pill tone={STATUS_TONES[status] || "muted"}>{status}</Pill>;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => {
          const e = info.row.original;
          return e.status === "enrolled" ? (
            <Button size="sm" color="danger" simple onClick={() => this.handleDrop(e.id)}>Drop</Button>
          ) : null;
        }
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <ViewToolbar tabs={[]} actions={[]} />
          {/* Stats */}
          <GridContainer style={{ marginBottom: "16px" }}>
            {[
              { label: "Total Enrollments", value: stats.total, color: "#1a237e" },
              { label: "Active", value: stats.active, color: "#2e7d32" },
              { label: "Dropped", value: stats.dropped, color: "#b71c1c" }
            ].map(s => (
              <GridItem xs={12} sm={4} key={s.label}>
                <div className={classes.statCard}>
                  <p className={classes.statNum} style={{ color: s.color }}>{s.value}</p>
                  <p className={classes.statLabel}>{s.label}</p>
                </div>
              </GridItem>
            ))}
          </GridContainer>

          {/* Filters */}
          <div className={classes.filterRow}>
            <FormControl className={classes.filterControl}>
              <InputLabel>Semester</InputLabel>
              <Select value={selectedSemester} onChange={e => this.setState({ selectedSemester: e.target.value }, this.fetchEnrollments)}>
                <MenuItem value="">— All Semesters —</MenuItem>
                {semesters.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}{s.isCurrent ? " (Current)" : ""} — {s.academicYear?.name || ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={classes.filterControl}>
              <InputLabel>Course</InputLabel>
              <Select value={selectedCourse} onChange={e => this.setState({ selectedCourse: e.target.value }, this.fetchEnrollments)}>
                <MenuItem value="">— All Courses —</MenuItem>
                {courses.map(c => <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.filterControl}>
              <InputLabel>Programme</InputLabel>
              <Select value={selectedProgramme} onChange={e => this.setState({ selectedProgramme: e.target.value }, this.fetchEnrollments)}>
                <MenuItem value="">— All Programmes —</MenuItem>
                {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Button size="sm" onClick={this.fetchEnrollments} color="primary" style={{ marginBottom: "2px" }}>
              Filter
            </Button>
          </div>

          <DataTable
            title="Course Enrollment"
            subtitle="Manage student course enrollments per semester"
            actions={(
              <div style={{ display: "flex", gap: "8px" }}>
                <Button size="sm" color="primary" onClick={() => this.setState({ bulkOpen: true, error: null })}>
                  Bulk Enroll
                </Button>
                <Button size="sm" color="primary" onClick={() => this.setState({
                  open: true, error: null,
                  form: { studentId: "", courseId: "", semesterId: selectedSemester || "" },
                  studentSearch: "", studentResults: []
                })}>
                  + Enroll Student
                </Button>
              </div>
            )}
            columns={columns}
            data={enrollments}
            isLoading={loading}
            emptyState={selectedSemester ? "No enrollments found for the selected filters." : "Select a semester to view enrollments."}
          />
        </GridItem>

        {/* Enroll Student Dialog */}
        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title="Enroll Student in Course" />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}

            {/* Student Search */}
            <div style={{ marginTop: "16px" }}>
              <InputLabel style={{ fontSize: "12px", marginBottom: "4px" }}>Search Student</InputLabel>
              <input
                type="text"
                placeholder="Type name or student number..."
                value={studentSearch}
                onChange={e => { this.setState({ studentSearch: e.target.value }); this.searchStudents(e.target.value); }}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }}
              />
              {searchLoading && <CircularProgress size={16} style={{ marginTop: "4px" }} />}
              {studentResults.length > 0 && (
                <div style={{ border: "1px solid #ddd", borderRadius: "4px", maxHeight: "150px", overflowY: "auto", marginTop: "4px" }}>
                  {studentResults.map(s => (
                    <div key={s.id}
                      onClick={() => this.setState({
                        form: { ...this.state.form, studentId: s.id },
                        studentSearch: `${s.studentNo} — ${s.firstName} ${s.lastName}`,
                        studentResults: []
                      })}
                      style={{ padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                      onMouseEnter={e => e.target.style.background = "#f5f5f5"}
                      onMouseLeave={e => e.target.style.background = "#fff"}
                    >
                      {s.studentNo} — {s.firstName} {s.lastName}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <FormControl className={classes.formControl}>
              <InputLabel>Course *</InputLabel>
              <Select value={form.courseId} onChange={e => this.setState({ form: { ...form, courseId: e.target.value } })}>
                <MenuItem value="">— Select Course —</MenuItem>
                {courses.map(c => <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl className={classes.formControl}>
              <InputLabel>Semester *</InputLabel>
              <Select value={form.semesterId} onChange={e => this.setState({ form: { ...form, semesterId: e.target.value } })}>
                <MenuItem value="">— Select Semester —</MenuItem>
                {semesters.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}{s.isCurrent ? " (Current)" : ""} — {s.academicYear?.name || ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleEnroll} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Enroll"}
            </Button>
          </DialogFooter>
        </Dialog>

        {/* Bulk Enrollment Dialog */}
        <Dialog open={bulkOpen} onClose={() => this.setState({ bulkOpen: false })} maxWidth="sm" fullWidth>
          <DialogHeader title="Bulk Enrollment" />
          <DialogContent>
            <p style={{ color: "#666", fontSize: "13px" }}>
              Auto-enroll all students in a programme/year into their semester courses based on the programme's course assignments.
            </p>
            {error && <p className={classes.errorMsg}>{error}</p>}
            {success && <p className={classes.successMsg}>{success}</p>}
            <FormControl className={classes.formControl}>
              <InputLabel>Programme *</InputLabel>
              <Select value={bulkForm.programmeId} onChange={e => this.setState({ bulkForm: { ...bulkForm, programmeId: e.target.value } })}>
                <MenuItem value="">— Select Programme —</MenuItem>
                {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Year of Study</InputLabel>
              <Select value={bulkForm.yearOfStudy} onChange={e => this.setState({ bulkForm: { ...bulkForm, yearOfStudy: e.target.value } })}>
                <MenuItem value="">— All Years —</MenuItem>
                {["1", "2", "3"].map(y => <MenuItem key={y} value={y}>Year {y}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Semester *</InputLabel>
              <Select value={bulkForm.semesterId} onChange={e => this.setState({ bulkForm: { ...bulkForm, semesterId: e.target.value } })}>
                <MenuItem value="">— Select Semester —</MenuItem>
                {semesters.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}{s.isCurrent ? " (Current)" : ""} — {s.academicYear?.name || ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ bulkOpen: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleBulkEnroll} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Run Bulk Enrollment"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

EnrollmentList.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(EnrollmentList));
